<template>
 <div class="my-container" aria-label="Page de connexion">

    <!-- ✅ En-tête dynamique -->
    <header
      class="page-header"
      :style="{ backgroundImage: `url(${headerImage})` }"
    >
      <h1>{{ translatedTitle }}</h1>
      <p class="p-header">{{ translatedDescription }}</p>
    </header>

    <!-- ✅ Formulaire de connexion -->

    <!-- ✅ Section Explore pour encourager l'exploration -->
     <p class="login-intro text-center mb-5" id="login-form-info">
  Connectez-vous à votre compte pour reprendre votre apprentissage, accéder aux cours, articles, et bien plus encore sur Longoka.  
  L’accès est gratuit et sécurisé.
</p>

    <Login />

    <ExploreSection />
  
 </div>
</template>

<script setup>
import { ref, onMounted, watchEffect } from "vue";
import { useNuxtApp } from "#imports";
import { useHead } from "#app";
import Login from "@/components/Login.vue";
import ExploreSection from "@/components/ExploreSection.vue";

const { $translate } = useNuxtApp();
const headerImage = useState("headerImage"); // ✅ Récupère l'image globale

// ✅ Variables réactives pour stocker les traductions
const translatedTitle = ref("Connexion - Longoka");
const translatedDescription = ref(
  "Connectez-vous pour accéder à votre compte."
);

// ✅ Liste des langues supportées
const supportedLanguages = ["fr", "en", "es", "de", "it", "pt", "ru", "zh"];

// ✅ Fonction pour traduire dynamiquement le SEO
async function translateLoginSEO() {
  try {
    const userLang = navigator.language.split("-")[0]; // Ex: "fr" pour "fr-FR"
    const targetLang = supportedLanguages.includes(userLang) ? userLang : "fr"; // Vérifier si la langue est supportée

    console.log(
      "🌍 Détection automatique de la langue :",
      userLang,
      "-> Utilisation de :",
      targetLang
    );

    // ✅ Traduire les textes dynamiquement
    translatedTitle.value = await $translate(
      "Page de Connexion ",
      "fr",
      targetLang
    );
    translatedDescription.value = await $translate(
      "Connectez-vous pour accéder à votre compte et continuer votre apprentissage.",
      "fr",
      targetLang
    );
  } catch (error) {
    console.error("❌ Erreur de traduction SEO :", error);
  }
}

// ✅ Exécuter la traduction et mettre à jour le SEO
onMounted(async () => {
  await translateLoginSEO();
});

// ✅ Appliquer le SEO après la traduction
watchEffect(() => {
  useHead({
 
   title: computed(() => `${translatedTitle.value} | Longoka`),
    meta: [
      { name: "description", content: translatedDescription.value },
      {
        name: "keywords",
        content: "connexion, se connecter, Longoka, apprentissage, communauté",
      },
      { name: "robots", content: "index, follow" },
      { name: "googlebot", content: "index, follow" },
      { name: "bingbot", content: "index, follow" },
      {
        name: "yandex-verification",
        content: process.env.YANDEX_SITE_VERIFICATION,
      },
      {
        name: "google-site-verification",
        content: process.env.GOOGLE_SITE_VERIFICATION,
      },
      { name: "application-name", content: "Longoka" },
      { name: "theme-color", content: "#ff4500" },
    ],

    link: [
      { rel: "canonical", href: "https://www.longoka.com/login" },
      {
        rel: "alternate",
        hreflang: "fr",
        href: "https://www.longoka.com/login",
      },
      {
        rel: "alternate",
        hreflang: "en",
        href: "https://www.longoka.com/en/login",
      },
      {
        rel: "alternate",
        hreflang: "es",
        href: "https://www.longoka.com/es/login",
      },
      {
        rel: "alternate",
        hreflang: "de",
        href: "https://www.longoka.com/de/login",
      },
      {
        rel: "alternate",
        hreflang: "it",
        href: "https://www.longoka.com/it/login",
      },
      {
        rel: "alternate",
        hreflang: "pt",
        href: "https://www.longoka.com/pt/login",
      },
      {
        rel: "alternate",
        hreflang: "ru",
        href: "https://www.longoka.com/ru/login",
      },
      {
        rel: "alternate",
        hreflang: "zh",
        href: "https://www.longoka.com/zh/login",
      },
      {
        rel: "alternate",
        hreflang: "x-default",
        href: "https://www.longoka.com/login",
      },
    ],

    script: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: translatedTitle.value,
          url: "https://www.longoka.com/login",
          description: translatedDescription.value,
          inLanguage: "fr",
        }),
      },
       {
      type: "application/ld+json",
      children: JSON.stringify({
           
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": translatedTitle.value,
  "url": "https://www.longoka.com/login",
  "description": translatedDescription.value,
  "inLanguage": "fr",
  "potentialAction": {
    "@type": "SignInAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": "https://www.longoka.com/login",
      "actionPlatform": [
        "http://schema.org/DesktopWebPlatform",
        "http://schema.org/MobileWebPlatform"
      ]
    }
  }

      })
    }
      ,
  

    ],
  });
});
</script>

<style scoped>
/* ✅ Container principal */
.login-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}
.login-intro {
  font-size: 1rem;
  color: #444;
  line-height: 1.6;
  max-width: 700px;
  margin: 0 auto;
}

/* ✅ Page Header */
.page-header {
  text-align: center;
  padding: 60px 20px;
  background-size: cover;
  background-position: center;
  color: #fff;
  border-radius: 10px;
}

.page-header h1 {
  font-size: 2.5rem;
  margin-bottom: 10px;
}

.p-header {
  font-size: 1.2rem;
}

/* ✅ Responsivité */
@media (max-width: 768px) {
  .page-header {
    padding: 40px 15px;
  }

  .page-header h1 {
    font-size: 2rem;
  }

  .p-header {
    font-size: 1rem;
  }
}
</style>
